import { useMemo } from 'react';
import './QualityPuzzle.css';

interface QualityPuzzleProps {
  /** Path to the diagram screenshot (placed in /public). */
  src?: string;
  alt?: string;
  className?: string;
  /** Number of energy particles in the background. */
  particleCount?: number;
}

interface Particle {
  x: number;
  y: number;
  size: number;
  depth: number; // 0 = far (blurred, faint), 1 = near (sharp, bright)
  delay: number;
  duration: number;
  drift: number;
}

function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildParticles(count: number): Particle[] {
  const rng = mulberry32(73);
  return Array.from({ length: count }, () => {
    const depth = rng();
    return {
      x: rng() * 100,
      y: rng() * 100,
      size: 3 + depth * 9,
      depth,
      delay: rng() * 8,
      duration: 7 + rng() * 9,
      drift: (rng() - 0.5) * 40,
    };
  });
}

/**
 * Flat diagram screenshot floating above a dynamic, glowing energy-particle
 * background. Particles vary in size/blur/brightness for a layered 3D depth,
 * with slowly drifting energy blobs behind them. Reduced-motion aware.
 */
export function QualityPuzzle({
  src = '/sqm-consultant/sqm-overview.png',
  alt = 'SQM intelligent quality system overview',
  className,
  particleCount = 80,
}: QualityPuzzleProps) {
  const particles = useMemo(() => buildParticles(particleCount), [particleCount]);

  return (
    <div className={`qz${className ? ` ${className}` : ''}`}>
      {/* Energy particle background */}
      <div className="qz__bg" aria-hidden>
        <span className="qz__blob qz__blob--1" />
        <span className="qz__blob qz__blob--2" />
        <span className="qz__blob qz__blob--3" />
        {particles.map((p, i) => (
          <span
            key={i}
            className="qz__particle"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              opacity: 0.25 + p.depth * 0.6,
              filter: `blur(${(1 - p.depth) * 2.4}px)`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              ['--drift' as string]: `${p.drift}px`,
            }}
          />
        ))}
      </div>

      {/* Flat screenshot */}
      <figure className="qz__frame">
        <img className="qz__img" src={src} alt={alt} loading="lazy" />
      </figure>
    </div>
  );
}
